import { clamp, debounce } from '@src/utils/functions/utils'
import { RefObject } from 'react'

import Product from '@src/quince-site-common/src/domains/Product'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'

export const SCALE_FACTOR = 3
export const ZOOMED_IN_IMAGE_CONTAINER_WIDTH_AT_1440 = 446
export const ZOOMED_IN_IMAGE_CONTAINER_HEIGHT_AT_1440 = 609

export interface ImageContainerMouseMoveParams {
  eventData: { clientX: number; clientY: number }
  getPxFromRem: (containerDimensionValueInRem: number, withUnit: boolean) => string | number
  imageContainerRef: RefObject<HTMLDivElement>
  hoverContainerRef: RefObject<HTMLDivElement>
  sideBoxImageContainerRef: RefObject<HTMLDivElement>
  sideBoxParentContainerRef: RefObject<HTMLDivElement>
  zoomedInImageContainerWidthAt1440?: number
  zoomedInImageContainerHeightAt1440?: number
}

export interface ImageContainerMouseLeaveParams {
  hoverContainerRef: RefObject<HTMLDivElement>
  sideBoxImageContainerRef: RefObject<HTMLDivElement>
  sideBoxParentContainerRef: RefObject<HTMLDivElement>
}

export interface HoverBoxPositionParams {
  zoomedInImageContainerWidth: number
  zoomedInImageContainerHeight: number
  mouseX: number
  mouseY: number
  imageContainerWidth: number
  imageContainerHeight: number
  scaleFactor?: number
}

/**
 * Calculates the position at which hover box is to be displayed in the image container
 * when mouse is at position {mouseX, mouseY}
 * for image container dimensions {imageContainerWidth, imageContainerHeight}
 * and zoomed in box dimensions {zoomedInImageContainerWidth, zoomedInImageContainerHeight}
 * Hover box is translated to the resulting hoverBoxXPosition and hoverBoxYPosition
 * */
export const getHoverBoxPosition = ({
  zoomedInImageContainerWidth,
  zoomedInImageContainerHeight,
  mouseX,
  mouseY,
  imageContainerWidth,
  imageContainerHeight,
  scaleFactor = SCALE_FACTOR,
}: HoverBoxPositionParams) => {
  const hoverBoxContainerHalfWidth = zoomedInImageContainerWidth / (2 * scaleFactor)
  const hoverBoxContainerHalfHeight = zoomedInImageContainerHeight / (2 * scaleFactor)

  const hoverBoxMinXPosition = hoverBoxContainerHalfWidth
  const hoverBoxMaxXPosition = imageContainerWidth - hoverBoxMinXPosition
  const hoverBoxMinYPosition = hoverBoxContainerHalfHeight
  const hoverBoxMaxYPosition = imageContainerHeight - hoverBoxMinYPosition
  const hoverBoxXPosition = clamp(mouseX, hoverBoxMinXPosition, hoverBoxMaxXPosition) - hoverBoxMinXPosition
  const hoverBoxYPosition =
    clamp(mouseY, hoverBoxMinYPosition, hoverBoxMaxYPosition) - hoverBoxMinYPosition - imageContainerHeight

  return {
    hoverBoxXPosition: Number(hoverBoxXPosition.toFixed(2)),
    hoverBoxYPosition: Number(hoverBoxYPosition.toFixed(2)),
  }
}

export const handleImageContainerMouseMove = ({
  eventData,
  getPxFromRem,
  imageContainerRef,
  hoverContainerRef,
  sideBoxImageContainerRef,
  sideBoxParentContainerRef,
  zoomedInImageContainerWidthAt1440 = ZOOMED_IN_IMAGE_CONTAINER_WIDTH_AT_1440,
  zoomedInImageContainerHeightAt1440 = ZOOMED_IN_IMAGE_CONTAINER_HEIGHT_AT_1440,
}: ImageContainerMouseMoveParams) => {
  const zoomedInImageContainerWidth = Number(getPxFromRem(zoomedInImageContainerWidthAt1440, false)) / 10
  const zoomedInImageContainerHeight = Number(getPxFromRem(zoomedInImageContainerHeightAt1440, false)) / 10
  if (imageContainerRef.current) {
    const rect = imageContainerRef.current.getBoundingClientRect()
    const imageContainerWidth = rect.width
    const imageContainerHeight = rect.height
    const mouseX = eventData.clientX - rect.left // x position with respect to the imageContainerRef left
    const mouseY = eventData.clientY - rect.top // y position with respect to the imageContainerRef top

    const { hoverBoxXPosition, hoverBoxYPosition } = getHoverBoxPosition({
      zoomedInImageContainerWidth,
      zoomedInImageContainerHeight,
      mouseX,
      mouseY,
      imageContainerWidth,
      imageContainerHeight,
    })

    if (hoverContainerRef.current) {
      hoverContainerRef.current.setAttribute(
        'style',
        `transform: scale(1)  translate(${hoverBoxXPosition}px, ${hoverBoxYPosition}px); display: block;`,
      )
    }
    if (sideBoxImageContainerRef.current) {
      const mouseXByLimit = clamp(
        mouseX,
        zoomedInImageContainerWidth / (2 * SCALE_FACTOR),
        imageContainerWidth - zoomedInImageContainerWidth / (2 * SCALE_FACTOR),
      )
      const mouseYByLimit = clamp(
        mouseY,
        zoomedInImageContainerHeight / (2 * SCALE_FACTOR),
        imageContainerHeight - zoomedInImageContainerHeight / (2 * SCALE_FACTOR),
      )
      const xVal = zoomedInImageContainerWidth / 2 - SCALE_FACTOR * mouseXByLimit
      const yVal = zoomedInImageContainerHeight / 2 - SCALE_FACTOR * mouseYByLimit
      const styles = `transform: scale(1) translate(${xVal}px, ${yVal}px); width: ${
        SCALE_FACTOR * imageContainerWidth
      }px; height: ${SCALE_FACTOR * imageContainerHeight}px;`
      sideBoxImageContainerRef.current.setAttribute('style', styles)
    }
    if (sideBoxParentContainerRef.current) {
      sideBoxParentContainerRef.current.setAttribute('style', 'overflow: hidden; display: block;')
    }
  }
}

export const handleImageContainerMouseLeave = ({
  hoverContainerRef,
  sideBoxImageContainerRef,
  sideBoxParentContainerRef,
}: ImageContainerMouseLeaveParams) => {
  if (hoverContainerRef.current) {
    hoverContainerRef.current.setAttribute('style', `transform: scale(1)  translate(0px, 0px);  display: none;`)
  }
  if (sideBoxParentContainerRef.current) {
    sideBoxParentContainerRef.current.setAttribute('style', 'display: none; overflow: hidden;')
  }
  if (sideBoxImageContainerRef.current) {
    sideBoxImageContainerRef.current.setAttribute('style', `transform: scale(1) translate(0px, 0px)`)
  }
}

export const productGalleryScrollEventTrigger = (
  product: Product | undefined,
  variantId: string | undefined,
  delay = 200,
) => {
  return debounce(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_GALLERY_SCROLLED, {
      name: product?.title,
      department: product?.classification?.department?.name,
      division: product?.classification?.division?.name,
      subdepartment: product?.classification?.subDepartment?.name,
      product_id: product?.shopifyProductId,
      productProperties: getProductPropertiesForTracking(product as Product, variantId),
    })
  }, delay)
}
